import { TFunction } from "i18next";
import { Column, ColumnGroup, Grouping } from "components/column-options";
import { ExpandedColumn } from "types/columns";

/** Category of each Payment Plans column; the array order is the group order. */
const categories: { key: string; columns: string[] }[] = [
  {
    key: "status",
    columns: [
      "hasPayments",
      "isPaid",
      "hasDiscountPayments",
      "isDiscountPaid",
      "reminderNumber",
    ],
  },
  {
    key: "policy",
    columns: [
      "policyName",
      "policyNumber",
      "policySubNumber",
      "internalNumber",
      "licensePlateNumber",
      "vehicleIdentificationNumber",
      "insuranceLineCode",
      "policyStatusCodeID",
      "policyProcessStatusCodeID",
      "policyVersionStatusCodeID",
      "policyPremiumCollectionTypeCodeID",
      "policyPaymentFrequencyCodeID",
      "premiumPaymentMeanCodeID",
      "validFrom",
      "expiry",
      "costCenterCodeID",
      "grecoCompanyNumber",
      "comment",
    ],
  },
  {
    key: "client",
    columns: [
      "client",
      "clientId",
      "clientName",
      "clientLastName",
      "clientFirstName",
      "clientNumber",
      "clientInternalNumber",
      "clientCompanyRegisterNumber",
      "clientPersonIdentificationNumber",
      "clientCity",
      "clientStreet",
      "clientZipCode",
      "clientEmailAddress",
      "clientPhoneNumber",
    ],
  },
  {
    key: "insurerPayer",
    columns: [
      "insurerName",
      "insurerId",
      "insurerInternalNumber",
      "payer",
      "payerId",
      "payerInternalNumber",
    ],
  },
  {
    key: "dates",
    columns: ["bookingDate", "premiumFrom", "premiumTo", "documentDate"],
  },
  {
    key: "premium",
    columns: [
      "premiumAmount",
      "premiumAmountWithoutTax",
      "premiumTaxAmount",
      "totalPremiumAmount",
      "paidPremiumAmount",
      "premiumDebt",
      "grossPremiumPVL",
      "premiumInclCommissionExclTaxes",
      "premiumTypeCodeID",
      "currencyCode",
    ],
  },
  {
    key: "discount",
    columns: [
      "premiumDiscount",
      "premiumDiscountPercent",
      "paidPremiumDiscountAmount",
      "premiumDiscountDebt",
    ],
  },
  {
    key: "commission",
    columns: ["commissionRate", "commissionAmount", "paidCommissionAmount"],
  },
  {
    key: "insurerReport",
    columns: [
      "insurerReportNumber",
      "insurerReportDate",
      "insurerReportPaymentDate",
      "insurerReportPremiumAmount",
      "insurerReportPremiumAmountWithoutTax",
      "insurerReportCommissionAmount",
      "insurerReportCommissionRate",
    ],
  },
];

const fieldTypes = ["text", "number", "date", "taxonomy", "boolean", "none"];

export const paymentPlanColumnGroupings = (
  t: TFunction,
  defaultColumns: ExpandedColumn[]
): Grouping[] => {
  const otherGroup: ColumnGroup = {
    key: "other",
    label: t("pct.columnGroup.other"),
    order: categories.length,
  };

  const categoryOf = (column: Column): ColumnGroup => {
    const index = categories.findIndex((c) => c.columns.includes(column.key));
    if (index === -1) return otherGroup;
    const key = categories[index].key;
    return { key, label: t(`pct.columnGroup.${key}`), order: index };
  };

  const fieldTypeOf = (column: Column): ColumnGroup => {
    const type = column.filter?.type ?? "none";
    return {
      key: type,
      label: t(`pct.columnGroup.fieldType.${type}`),
      order: fieldTypes.indexOf(type),
    };
  };

  const defaultVisibilityOf = (column: Column): ColumnGroup => {
    const isShown = !!defaultColumns.find((c) => c.key === column.key)
      ?.isVisible;
    return isShown
      ? { key: "shown", label: t("pct.columnGroup.shownByDefault"), order: 0 }
      : { key: "hidden", label: t("pct.columnGroup.hiddenByDefault"), order: 1 };
  };

  return [
    { key: "category", label: t("pct.columnGroup.byCategory"), groupOf: categoryOf },
    { key: "fieldType", label: t("pct.columnGroup.byFieldType"), groupOf: fieldTypeOf },
    {
      key: "defaultVisibility",
      label: t("pct.columnGroup.byDefaultVisibility"),
      groupOf: defaultVisibilityOf,
    },
    { key: "alphabetical", label: t("greco.alphabetical"), groupOf: () => null },
  ];
};
